import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Typography, Form, Select } from 'antd';
import {
  ECellValueDisplayMode,
  ECellValueType,
  ExcelTableColDef,
  EExcelTableColumnType,
  EExcelTablePaginationType,
  RowData,
} from 'components/ui/excel-table/types';
import { useMemo, useState, useEffect, useCallback } from 'react';
import ExcelTable from 'components/ui/excel-table';
import FilterIcon from 'assets/icons/filter';
import ExportV2Icon from 'assets/icons/export-v2';
import SearchIcon from 'assets/icons/search';
import { CountrySelection } from 'components/ui/country-selection';
import { TOTAL_ROW_KEY } from 'components/ui/excel-table/constants';
import {
  FireRatingLayerConnectionDto,
  GetTailoredResultQueryDtoSeverityEnum,
  SubmissionResponseDto,
  UpdateScheduleOfValuesPayloadDto,
} from 'libs/pinnacle-sdk';
import { priceRatingApi, scheduleOfValuesApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { useMutation, useQuery } from 'react-query';
import usePagination from 'hooks/usePagination';
import { handleError } from 'utils/form';
import { validateFirePricingAnalysisRow } from './forms';
import { TFunction } from 'i18next';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import { FirePricingAnalysisRow } from './types';
import { isSubmissionEditable } from 'utils/submission';
import { SortedSelect } from 'components/ui/sorted-select';

const { Title } = Typography;

const FirePricingAnalysis: React.FC<{
  submission: SubmissionResponseDto;
  currency: string;
}> = ({ submission, currency }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    currentPage,
    pageSize,
    onPageChange,
    resetPagination,
    onShowSizeChange,
  } = usePagination();
  const [severity] = useState<
    GetTailoredResultQueryDtoSeverityEnum | undefined
  >();

  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submission.status);
  }, [submission.status]);

  const countries = Form.useWatch('countries', form);
  const occupancies = Form.useWatch('occupancies', form);
  const searchText = Form.useWatch('searchText', form);

  const shouldEnableEditing = useMemo(() => {
    if (!submission.originalCurrency) return isEditingMode;

    return currency === submission.originalCurrency && isEditingMode;
  }, [currency, submission.originalCurrency, isEditingMode]);

  const { data: countryListData } = useQuery(
    [EQueryKeys.SOV_COUNTRIES, submission.submissionId],
    () =>
      scheduleOfValuesApi.getSovCountryList({
        submissionId: submission.submissionId,
      })
  );

  const { data: occupancyListData, isLoading: isOccupancyListLoading } =
    useQuery([EQueryKeys.SOV_OCCUPANCIES, submission.submissionId], () =>
      scheduleOfValuesApi.getSovOccupancyList({
        submissionId: submission.submissionId,
      })
    );

  useEffect(() => {
    resetPagination();
  }, [countries, occupancies, resetPagination, searchText]);

  const { data: firePricingAnalysis, isLoading: isFirePricingAnalysisLoading } =
    useQuery(
      [
        EQueryKeys.FIRE_PRICING_ANALYSIS,
        submission.submissionId,
        currentPage,
        pageSize,
        searchText,
        severity,
        countries,
        occupancies,
        currency,
      ],
      () =>
        priceRatingApi.getFirePricingAnalysis(submission.submissionId, {
          page: currentPage,
          pageSize,
          searchText,
          currency,
          severity,
          countries,
          occupancies,
          includeTotalRow: true,
        }),
      {
        keepPreviousData: true,
      }
    );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'occupancy',
        headerName: t('firePricingAnalysis.occupancy'),
        minWidth: 250,
        flex: 1,
        editable: false,
        resizable: true,
      },
      {
        field: 'property',
        headerName: t('firePricingAnalysis.property'),
        minWidth: 200,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'suburb',
        headerName: t('firePricingAnalysis.suburb'),
        minWidth: 180,
        flex: 1,
        editable: false,
        resizable: true,
      },
      {
        field: 'fireMultiplier',
        headerName: t('firePricingAnalysis.multiplier'),
        minWidth: 120,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: shouldEnableEditing,
        resizable: true,
      },
      {
        field: 'pdValue',
        headerName: t('firePricingAnalysis.pdValue'),
        minWidth: 150,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        resizable: true,
        precision: 0,
      },
      {
        field: 'pdRate',
        headerName: t('firePricingAnalysis.pdRate'),
        minWidth: 120,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: shouldEnableEditing,
        precision: 3,
        resizable: true,
      },
      {
        field: 'biValue',
        headerName: t('firePricingAnalysis.biValue'),
        minWidth: 150,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        resizable: true,
        precision: 0,
      },
      {
        field: 'biRate',
        headerName: t('firePricingAnalysis.biRate'),
        minWidth: 120,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: shouldEnableEditing,
        precision: 3,
        resizable: true,
      },
      {
        field: 'tiv',
        headerName: t('firePricingAnalysis.tiv'),
        minWidth: 150,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        resizable: true,
        precision: 0,
      },
      {
        field: 'fireRiskPremium',
        headerName: t('firePricingAnalysis.fireRiskPremium'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        resizable: true,
        precision: 0,
      },
      {
        field: 'severity',
        headerName: t('firePricingAnalysis.severity'),
        minWidth: 150,
        flex: 1,
        editable: shouldEnableEditing,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions: [
          {
            value: GetTailoredResultQueryDtoSeverityEnum.High,
            label: 'High',
            style: { color: '#FF4D4F' },
          },
          {
            value: GetTailoredResultQueryDtoSeverityEnum.Average,
            label: 'Average',
            style: { color: '#FAAD14' },
          },
          {
            value: GetTailoredResultQueryDtoSeverityEnum.Low,
            label: 'Low',
            style: { color: '#52C41A' },
          },
        ],
        resizable: true,
      },
    ],
    [t, shouldEnableEditing]
  );

  const layerColumns: ExcelTableColDef[] = [];
  firePricingAnalysis?.items?.[0]?.layerConnections?.forEach(
    (layerConnection) => {
      layerColumns.push({
        field: `layer_${layerConnection.layerId}`,
        headerName: `${t('firePricingAnalysis.layer')} ${layerConnection.layerNumber} ${t('firePricingAnalysis.expectedLossCost')}`,
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        resizable: true,
        valueGetter: (params) => {
          const item = params.data;
          if (item.layerConnections) {
            const matchingConnection = item.layerConnections.find(
              (conn: FireRatingLayerConnectionDto) =>
                conn.layerId === layerConnection.layerId
            );
            return matchingConnection
              ? matchingConnection.expectedLostCost
              : null;
          }

          return;
        },
        precision: 0,
      });
    }
  );

  const updateSovSettingsMutation = useMutation(
    async (
      updateScheduleOfValuesPayloadDto: Array<UpdateScheduleOfValuesPayloadDto>
    ) => {
      await scheduleOfValuesApi.updateScheduleOfValues({
        updateScheduleOfValuesPayloadDto,
      });
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t(
            'firePricingAnalysis.updateFirePricingAnalysisFailed'
          ),
        });
      },
    }
  );

  const onDataChange = useCallback(
    async (affectedRow: RowData<FirePricingAnalysisRow>) => {
      if (affectedRow.isError.strict) return;

      await updateSovSettingsMutation.mutateAsync([
        {
          submissionId: submission.submissionId,
          severity: affectedRow.data.severity,
          fireMultiplier: affectedRow.data.fireMultiplier?.toString(),
          sovId: affectedRow.data.sovId,
          pdRate: affectedRow.data.pdRate
            ? (Number(affectedRow.data.pdRate) / 100).toString() // Convert from percentage to decimal
            : undefined,
          biRate: affectedRow.data.biRate
            ? (Number(affectedRow.data.biRate) / 100).toString() // Convert from percentage to decimal
            : undefined,
        },
      ]);
    },
    [submission.submissionId, updateSovSettingsMutation]
  );

  const validateRow = useCallback(
    (
      row: FirePricingAnalysisRow,
      rowIndex: number,
      _: FirePricingAnalysisRow[],
      t: TFunction
    ) => {
      return validateFirePricingAnalysisRow(row, rowIndex, t);
    },
    []
  );

  const rowData: FirePricingAnalysisRow[] = useMemo(() => {
    return (
      firePricingAnalysis?.items.map((item) => ({
        ...item,
        fireMultiplier: Number(item.fireMultiplier),
        pdRate: Number(item.pdRate),
        biRate: Number(item.biRate),
      })) || []
    );
  }, [firePricingAnalysis]);

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <Form form={form} className="w-full">
        <div className="flex items-center justify-between">
          <Title level={4} className="ml-4">
            {t('firePricingAnalysis.firePricingAnalysis')}
          </Title>

          <div className="flex gap-2">
            <Form.Item
              name="searchText"
              className="m-0"
              style={{ width: '320px' }}
            >
              <Input
                prefix={<SearchIcon />}
                placeholder={t('general.search')}
                size="large"
                className="rounded-xl shadow-md"
              />
            </Form.Item>
            <button className="p-2 rounded-xl shadow-md border hover:bg-gray-50">
              <FilterIcon />
            </button>
            <button className="flex items-center gap-2 px-6 py-2 shadow-md bg-[#1d67b6] text-white rounded-lg hover:bg-[#1d67b6]/90 transition-colors">
              <ExportV2Icon />
              {t('firePricingAnalysis.export')}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-4">
          <div className="w-64">
            <CountrySelection
              name="countries"
              label=""
              includeCountryFlag={false}
              countryList={countryListData}
              mode="multiple"
              maxTagCount={1}
            />
          </div>

          <div className="min-w-64">
            <Form.Item name="occupancies" label="" className="mb-4 w-full">
              <SortedSelect
                options={occupancyListData?.map((occupancy) => ({
                  value: occupancy,
                  label: occupancy,
                }))}
                allowClear
                maxTagCount={1}
                showSearch
                size="large"
                placeholder={t('firePricingAnalysis.selectOccupancy')}
                loading={isOccupancyListLoading}
                suffixIcon={<ArrowDownIcon />}
                mode="multiple"
              />
            </Form.Item>
          </div>
        </div>
      </Form>

      <ExcelTable
        data={rowData}
        columnDefs={[...columnDefs, ...layerColumns]}
        paginationType={EExcelTablePaginationType.DETAIL}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRows={firePricingAnalysis?.pagination.total || 0}
        rowKeyColName="sovId"
        baseTableHeight={490}
        tableHeightBias={350}
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={false}
        includeAddRow={false}
        includeDeleteCol={false}
        validateRow={validateRow}
        summaryRowData={
          firePricingAnalysis?.totalRow
            ? ({
                sovId: TOTAL_ROW_KEY,
                layerConnections: [],
                occupancy: t(
                  'general.grandTotal'
                ) as GetTailoredResultQueryDtoSeverityEnum,
                ...firePricingAnalysis.totalRow,
              } as unknown as FirePricingAnalysisRow)
            : undefined
        }
        loading={isFirePricingAnalysisLoading}
        onDataChange={isEditingMode ? onDataChange : undefined}
      />
    </div>
  );
};

export default FirePricingAnalysis;
